import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * useWalletBalance — small hook that fetches the vendor's split
 * wallet balance and exposes a manual refresh. Consumed by the 3
 * wallet-pay checkouts (campaign fee, listing duration, featured
 * slot) so they all render the same balance widget from a single
 * source of truth.
 *
 * Returns:
 *   {
 *     loading,          // true on initial mount + during refresh()
 *     error,            // string | null
 *     balancePaise,     // combined total (promo + real)
 *     realPaise,        // funded by Razorpay top-ups
 *     promoPaise,       // funded by SuperAdmin grants
 *     refresh,          // async () => refetch balances
 *     setBalancePaise,  // manual optimistic bump (used after
 *                       // insufficient-balance 402 to swap in the
 *                       // server-echoed balance without a round-trip)
 *   }
 *
 * The hook fires once on mount. Callers refresh explicitly after
 * a debit succeeds so the UI shows the fresh balance without a
 * full page reload.
 */
export function useWalletBalance({ autoLoad = true } = {}) {
  const [state, setState] = useState({
    loading: !!autoLoad,
    error: null,
    balancePaise: 0,
    realPaise: 0,
    promoPaise: 0,
  });

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const r = await api.walletSummary();
      setState({
        loading: false,
        error: null,
        balancePaise: Number(r.data?.balance_paise || 0),
        realPaise: Number(r.data?.real_paise ?? r.data?.balance_paise ?? 0),
        promoPaise: Number(r.data?.promo_paise || 0),
      });
    } catch (e) {
      setState((s) => ({
        ...s,
        loading: false,
        error: e?.response?.data?.detail || "Couldn't load wallet balance",
      }));
    }
  }, []);

  const setBalancePaise = useCallback((next) => {
    // Optimistic update after a 402 tells us the exact server-side
    // balance. We keep the promo/real split as-is (the server may
    // send `balance_paise` only in the error detail, not the split).
    setState((s) => ({
      ...s,
      balancePaise: Number(next || 0),
    }));
  }, []);

  useEffect(() => {
    if (autoLoad) refresh();
  }, [autoLoad, refresh]);

  return { ...state, refresh, setBalancePaise };
}
